import { useEffect, useRef, useState } from 'react';

import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  getWorkspaceAgentLiveAnnouncementUpdate,
  resolveWorkspaceAgentLiveAnnouncementShouldSeedSessionBaseline,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentLiveAnnouncement';

export type UseWorkspaceAgentMessageLiveAnnouncementParams = {
  isLoadingMessages: boolean;
  messages: readonly WorkspaceAgentMessage[];
  sessionId: string | null;
};

/** Announces new tail messages without relying on `role="log"` inside a virtualized list. */
export const useWorkspaceAgentMessageLiveAnnouncement = ({
  isLoadingMessages,
  messages,
  sessionId,
}: UseWorkspaceAgentMessageLiveAnnouncementParams): string => {
  const previousLastMessageIdRef = useRef<string | null>(null);
  const previousMessageCountRef = useRef(0);
  const previousSessionIdRef = useRef<string | null | undefined>(undefined);
  const shouldSeedSessionBaselineRef = useRef<boolean>(false);
  const wasLoadingMessagesRef = useRef<boolean>(false);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (sessionId === previousSessionIdRef.current) {
      return;
    }

    const isFirstSessionObservation = previousSessionIdRef.current === undefined;
    previousSessionIdRef.current = sessionId;
    previousLastMessageIdRef.current = null;
    previousMessageCountRef.current = 0;
    shouldSeedSessionBaselineRef.current = !isFirstSessionObservation;
    wasLoadingMessagesRef.current = false;
    setAnnouncement('');
  }, [sessionId]);

  useEffect(() => {
    if (isLoadingMessages) {
      wasLoadingMessagesRef.current = true;
    }

    const nextShouldSeedSessionBaseline = resolveWorkspaceAgentLiveAnnouncementShouldSeedSessionBaseline({
      isLoadingMessages,
      messageCount: messages.length,
      shouldSeedSessionBaseline: shouldSeedSessionBaselineRef.current,
      wasLoadingMessages: wasLoadingMessagesRef.current,
    });
    shouldSeedSessionBaselineRef.current = nextShouldSeedSessionBaseline;

    const update = getWorkspaceAgentLiveAnnouncementUpdate({
      isLoadingMessages,
      messages,
      previousLastMessageId: previousLastMessageIdRef.current,
      previousMessageCount: previousMessageCountRef.current,
      shouldSeedSessionBaseline: nextShouldSeedSessionBaseline,
      wasLoadingMessages: wasLoadingMessagesRef.current,
    });

    previousLastMessageIdRef.current = update.nextLastMessageId;
    previousMessageCountRef.current = update.nextMessageCount;

    if (update.action === 'seed' || update.action === 'announce') {
      shouldSeedSessionBaselineRef.current = false;
    }

    if (update.action === 'announce') {
      setAnnouncement(update.announcement);
    }
  }, [isLoadingMessages, messages]);

  return announcement;
};
